import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchServices, fetchCategories, fetchAllTags, updateService } from '../services/api';
import CategorySelect from '../components/CategorySelect';

const EditService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const [user, setUser] = useState(null);
  const [service, setService] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Working hours state
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    pricing_type: 'fixed', // Default to fixed price
    category_id: '',
  });

  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth');
      return;
    }
    try {
      const parsed = JSON.parse(userData);
      if (parsed.user_type !== 'vendor') {
        navigate('/');
        return;
      }
      setUser(parsed);
      loadData(parsed, serviceId);
    } catch (err) {
      navigate('/auth');
    }
  }, [navigate, serviceId, location]);

  const loadData = async (parsedUser, id) => {
    try {
      setLoading(true);
      const [servicesData, cats, tgs] = await Promise.all([
        fetchServices({ user_id: parsedUser.id }),
        fetchCategories(),
        fetchAllTags(),
      ]);

      const servicesList = servicesData.data || servicesData;
      const currentService = Array.isArray(servicesList)
        ? servicesList.find(s => s.id === parseInt(id))
        : null;

      if (!currentService) {
        setError('Service not found');
        return;
      }

      setService(currentService);
      setFormData({
        name: currentService.name,
        description: currentService.description,
        price: currentService.price,
        pricing_type: currentService.pricing_type || 'fixed',
        category_id: currentService.category_id,
      });

      setCategories(cats || []);
      setAllTags(tgs || []);

      // Parse working hours
      if (currentService.hours) {
        parseHours(currentService.hours);
      }

      // Load tags for this service (if they exist)
      if (currentService.tags && Array.isArray(currentService.tags)) {
        setSelectedTags(currentService.tags);
      }
    } catch (err) {
      setError('Failed to load service data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseHours = (hoursString) => {
    try {
      // Example format: "Mon, Tue, Wed, Thu, Fri (09:00-18:00)"
      const match = hoursString.match(/(.+?)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
      if (match) {
        const daysStr = match[1];
        const start = match[2];
        const end = match[3];
        const days = daysStr.split(',').map(d => d.trim());
        setWorkingDays(days);
        setTimeRange({ start, end });
      }
    } catch (err) {
      console.error('Error parsing hours:', err);
    }
  };

  // --- Tag filtering effect ---
  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(search) &&
        !selectedTags.some(selected => selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 8));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, selectedTags]);

  const toggleDay = (day) => {
    setWorkingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
    setTagSearch('');
    setFilteredTags([]);
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
  };

  const handleTagInputKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        // Check if tag exists in allTags
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          // For new tags, create a temporary object (will be handled by backend)
          const newTag = { id: null, name: tagName, temp: true };
          setSelectedTags(prev => [...prev, newTag]);
          setTagSearch('');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const hoursFormatted =
      workingDays.length > 0
        ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
        : 'Closed / Appointment Only';

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: formData.price,
        pricing_type: formData.pricing_type,
        category_id: parseInt(formData.category_id),
        hours: hoursFormatted,
        tags: selectedTags, // Array of tag objects (with id and name)
      };

      await updateService(serviceId, payload);
      alert('Service updated successfully!');
      navigate('/vendor-dashboard', { state: { refreshServices: true } });
    } catch (err) {
      setError(err.message || 'Failed to update service');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loader"></div>
      </div>
    );
  }

  if (!service || !user) return null;

  return (
    <div className="section" style={{ maxWidth: '720px' }}>
      <style>{`
        .day-pill { padding: 6px 12px; border: 1px solid var(--border); border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; transition: 0.2s; background: white; }
        .day-pill.active { background: var(--primary); color: white; border-color: var(--primary); }
        .tag-pill { padding: 6px 14px; border-radius: 20px; border: 1px solid var(--border); cursor: pointer; font-size: 12px; transition: 0.2s; background: var(--surface-2); }
        .tag-pill.active { background: var(--accent); color: white; border-color: var(--accent); }
        .suggestion-box { background: white; border: 1px solid var(--border); border-radius: 8px; max-height: 200px; overflow-y: auto; margin-bottom: 12px; }
        .suggestion-item { padding: 10px 12px; cursor: pointer; border-bottom: 1px solid var(--border); font-size: 13px; transition: 0.15s; background: white; }
        .suggestion-item:hover { background: var(--surface-2); }
        .suggestion-item:last-child { border-bottom: none; }
        .form-card { background: white; border: 1px solid var(--border); border-radius: 16px; padding: 24px; margin-bottom: 24px; }
      `}</style>

      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 800 }}>Edit Service</h1>
        <p style={{ color: 'var(--text-3)' }}>Update your service details below.</p>
      </div>

      {error && (
        <div
          style={{
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#991B1B',
          }}
        >
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Service Details */}
        <div className="form-card">
          <h3 style={{ marginTop: 0 }}>Service Details</h3>

          <div className="form-group">
            <label className="form-label">Service Name *</label>
            <input
              className="form-input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-input"
              rows="4"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>

          {/* Pricing Strategy Section */}
          <div style={{
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>
              💰 Pricing Strategy
            </h4>
            
            {/* Pricing Type Selection */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">Select Your Pricing Model *</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {[
                  { id: 'visiting', label: 'Visiting/Inspection Fee', icon: '🚗', description: 'Perfect for Electricians, Plumbers, AC Repair' },
                  { id: 'fixed', label: 'Fixed Rate', icon: '✅', description: 'Best for Salon, Car Wash, Tasks' },
                  { id: 'starting', label: 'Starting From', icon: '📈', description: 'Great for Painters, Carpenters, Projects' },
                  { id: 'subscription', label: 'Recurring/Subscription', icon: '🔄', description: 'Ideal for Milk, Tiffins, Tutors' },
                ].map(model => (
                  <div
                    key={model.id}
                    onClick={() => setFormData({ ...formData, pricing_type: model.id })}
                    style={{
                      padding: '12px',
                      border: formData.pricing_type === model.id ? '2px solid #4f46e5' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: formData.pricing_type === model.id ? '#eef2ff' : '#fff',
                      transition: '0.2s',
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '6px' }}>{model.icon}</div>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '13px',
                      color: formData.pricing_type === model.id ? '#4f46e5' : '#1e293b',
                      marginBottom: '4px'
                    }}>
                      {model.label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {model.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Input with Dynamic Label */}
            <div className="form-group">
              <label className="form-label">
                {formData.pricing_type === 'visiting' && 'Visiting/Inspection Fee (₹) *'}
                {formData.pricing_type === 'fixed' && 'Fixed Price (₹) *'}
                {formData.pricing_type === 'starting' && 'Starting Price (₹) *'}
                {formData.pricing_type === 'subscription' && 'Subscription Rate (₹) *'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>₹</span>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={formData.pricing_type === 'subscription' ? 'e.g., 500 per month' : 'Enter price'}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                {formData.pricing_type === 'visiting' && '✓ Charged per visit or inspection'}
                {formData.pricing_type === 'fixed' && '✓ One-time flat rate for the service'}
                {formData.pricing_type === 'starting' && '✓ Starting minimum for project-based work'}
                {formData.pricing_type === 'subscription' && '✓ Monthly or periodic recurring charge'}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Category *</label>
            <CategorySelect
              categories={categories}
              value={formData.category_id}
              onChange={(catId) => setFormData({ ...formData, category_id: catId })}
              required
            />
          </div>
        </div>

        {/* Working Hours */}
        <div className="form-card">
          <h3 style={{ marginTop: 0 }}>Working Hours</h3>

          <div className="form-group">
            <label className="form-label">Days of Operation</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {daysList.map((day) => (
                <div
                  key={day}
                  className={`day-pill ${workingDays.includes(day) ? 'active' : ''}`}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Opening Time</label>
              <input
                className="form-input"
                type="time"
                value={timeRange.start}
                onChange={(e) => setTimeRange({ ...timeRange, start: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Closing Time</label>
              <input
                className="form-input"
                type="time"
                value={timeRange.end}
                onChange={(e) => setTimeRange({ ...timeRange, end: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="form-card">
          <h3 style={{ marginTop: 0 }}>Search Keywords</h3>

          <label className="form-label">Tags (Optional - Press Enter to add)</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search or type a tag and press Enter..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            onKeyDown={handleTagInputKeyDown}
            style={{ marginBottom: '10px' }}
          />
          
          {filteredTags.length > 0 && (
            <div className="suggestion-box">
              {filteredTags.map(tag => (
                <div key={tag.id} onClick={() => handleTagSelect(tag)} className="suggestion-item">
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {selectedTags.map(tag => (
              <div key={tag.id || tag.name} className="tag-pill active">
                # {tag.name}
                <span
                  onClick={() => handleTagRemove(tag)}
                  style={{ marginLeft: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ✕
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ flex: 1 }}
          >
            {submitting ? 'Updating...' : 'Update Service'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/vendor-dashboard')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditService;
